import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
// The UI build bundles the player, so this is the only shaka entry point the player imports.
import shaka from 'shaka-player/dist/shaka-player.ui';
import { ProxyDrmSystem, ProxyPlaybackReady, offeredLicenseServers } from '@warehouse/object-browser/core';
import { ErrorAlert } from '@warehouse/shared/ui';
import { refetchNxObject } from '@warehouse/object-browser/infra';
import { ProxyPlayerSource, proxyPlayerRegistrySingleton } from '../domain/proxy-player-session';

/** How each DRM system is named to a person, as opposed to its EME key-system string. */
const DRM_SYSTEM_LABELS: Record<ProxyDrmSystem, string> = {
	widevine: 'Widevine',
	playready: 'PlayReady',
};

type LicenseServers = ProxyPlaybackReady['drm']['licenseServers'];

interface ShakaPlayerProps {
	nxObjectUuid: string;
	/** The source object's eTag. A new one means a new proxy; a refetch with the same one only re-signs the manifest. */
	eTag: string;
	playback: ProxyPlaybackReady;
}

/** Where a preview was when its media URLs expired, so the renewed one picks up from there. */
interface ResumePoint {
	time: number;
	playing: boolean;
}

/** A renewal waiting on a refetch to bring a manifest other than the expired one. */
interface PendingRenewal {
	expiredManifest: string;
	reloadWith(playback: ProxyPlayerSource): void;
}

/** How long a refetch has to bring a re-signed manifest before the expired preview is reported instead. */
const RENEWAL_TIMEOUT_MS = 15_000;

/** Below this, a renewed preview failing again is taken as a real failure even if the backend reports no expiry. */
const MIN_RENEWED_URL_LIFETIME_MS = 30_000;

export function ShakaPlayer({ nxObjectUuid, eTag, playback }: ShakaPlayerProps) {
	const slotRef = useRef<HTMLDivElement>(null);
	const [isPlayingElsewhere, setIsPlayingElsewhere] = useState(false);
	const [playbackError, setPlaybackError] = useState<string | undefined>(undefined);
	const [isDrmProtected, setIsDrmProtected] = useState(false);
	const pendingRenewalRef = useRef<PendingRenewal | undefined>(undefined);

	// Every object refetch re-signs the manifest into a new string, so it is read here rather than restarting the session.
	const playbackRef = useRef<ProxyPlaybackReady>(playback);
	playbackRef.current = playback;

	// What tells one proxy from another: a regenerate clears `outdated` and usually brings new content keys, while
	// a refetch that only re-signs the URLs keeps all three.
	const revision = `${eTag}|${playback.outdated ? 'outdated' : 'current'}|${playback.drm.keyIds.join(',')}`;

	useEffect(() => {
		const pendingRenewal = pendingRenewalRef.current;
		if (!pendingRenewal || playback.manifest === pendingRenewal.expiredManifest) return;
		pendingRenewalRef.current = undefined;
		pendingRenewal.reloadWith(playbackRef.current);
	}, [playback.manifest]);

	useEffect(() => {
		const slot = slotRef.current;
		if (!slot) return undefined;

		// Safari and older engines need the shims before any support question can be answered.
		shaka.polyfill.installAll();
		if (!shaka.Player.isBrowserSupported()) {
			setPlaybackError('This browser cannot play DASH streams.');
			return undefined;
		}

		let cancelled = false;
		let hasLoaded = false;
		let resumePoint: ResumePoint | undefined;
		let lastRenewedAt: number | undefined;
		let renewalTimeout: ReturnType<typeof setTimeout> | undefined;
		const registry = proxyPlayerRegistrySingleton.get();
		const session = registry.acquire(nxObjectUuid, revision, playbackRef.current);

		// The player is only borrowed: it may be in a window or parked off-screen when this view opens.
		const showHere = () => {
			const elsewhere = session.isInSeparateWindow();
			if (!elsewhere) session.placeIn(slot);
			// Shaka leaves its own placeholder in the slot it took the player from; a fresh slot is empty.
			setIsPlayingElsewhere(elsewhere && slot.childElementCount === 0);
		};
		showHere();
		const stopWatchingPlacement = session.onPlacementChange(showHere);

		const showError = (error: unknown) => {
			setPlaybackError(describeShakaError(error, playbackRef.current.drm.licenseServers));
		};

		const followLoad = (loading: Promise<void>) => {
			hasLoaded = false;
			loading
				.then(() => {
					if (cancelled) return;
					hasLoaded = true;
					setIsDrmProtected(!!session.player.drmInfo());
					resumeFrom(session.video, resumePoint);
					resumePoint = undefined;
				})
				.catch((error) => {
					if (!cancelled) showError(error);
				});
		};

		const reloadWith = (latest: ProxyPlayerSource) => {
			clearTimeout(renewalTimeout);
			if (cancelled) return;
			lastRenewedAt = Date.now();
			followLoad(session.reload(latest));
		};

		// Renewed URLs cannot expire before they have lived out their lifetime, so failing sooner is not expiry.
		const isTooSoonAfterRenewal = () => {
			if (lastRenewedAt === undefined) return false;
			const lifetimeMs = Math.max(MIN_RENEWED_URL_LIFETIME_MS, (playbackRef.current.expiresInSeconds * 1000) / 2);
			return Date.now() - lastRenewedAt < lifetimeMs;
		};

		// Presigned media URLs only live so long: a preview that was playing renews them rather than stopping.
		const renewExpiredManifest = (error: unknown) => {
			// Every segment in flight fails at once; the first one's renewal covers them all.
			if (pendingRenewalRef.current) return;
			if (isTooSoonAfterRenewal()) {
				showError(error);
				return;
			}
			resumePoint = { time: session.video.currentTime, playing: !session.video.paused };
			if (playbackRef.current.manifest !== session.manifest) {
				reloadWith(playbackRef.current);
				return;
			}
			pendingRenewalRef.current = { expiredManifest: session.manifest, reloadWith };
			renewalTimeout = setTimeout(() => {
				pendingRenewalRef.current = undefined;
				if (!cancelled) showError(error);
			}, RENEWAL_TIMEOUT_MS);
			refetchNxObject(nxObjectUuid);
		};

		// A licence can be refused after the stream has loaded, which never reaches the load() rejection.
		const onError = (event: Event) => {
			if (cancelled) return;
			const error = (event as CustomEvent).detail;
			if (hasLoaded && isExpiredMediaUrlError(error)) {
				renewExpiredManifest(error);
				return;
			}
			showError(error);
		};
		session.player.addEventListener('error', onError);

		setPlaybackError(undefined);
		followLoad(session.ready);

		return () => {
			cancelled = true;
			clearTimeout(renewalTimeout);
			pendingRenewalRef.current = undefined;
			session.player.removeEventListener('error', onError);
			stopWatchingPlacement();
			registry.release(session);
		};
	}, [nxObjectUuid, revision]);

	return (
		<PlayerWrapper>
			{playbackError && <ErrorAlert errorMessage={playbackError} />}
			<PlayerSlot ref={slotRef} hidden={!!playbackError} />
			{isPlayingElsewhere && (
				<SeparateWindowNote aria-label="Proxy Player Elsewhere">
					Playing in a separate window. Close that window to bring the preview back here.
				</SeparateWindowNote>
			)}
			{isDrmProtected && (
				<DrmBadge aria-label="DRM Status">
					<LockOutlinedIcon fontSize="inherit" />
					DRM-protected
				</DrmBadge>
			)}
		</PlayerWrapper>
	);
}

/** The Shaka error codes worth naming, and the reason each one stands for. */
const SHAKA_ERROR_REASONS: Record<number, (error: shaka.util.Error, licenseServers: LicenseServers) => string> = {
	[shaka.util.Error.Code.REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE]: (_, servers) => noUsableKeySystem(servers),
	[shaka.util.Error.Code.NO_RECOGNIZED_KEY_SYSTEMS]: (_, servers) => noUsableKeySystem(servers),
	[shaka.util.Error.Code.LICENSE_REQUEST_FAILED]: () => 'The DRM licence request was refused',
	[shaka.util.Error.Code.LICENSE_RESPONSE_REJECTED]: () => 'The DRM licence was rejected by this browser',
	[shaka.util.Error.Code.EXPIRED]: () => 'The DRM licence has expired',
	// Usually the presigned media URLs outliving the manifest's expiresInSeconds.
	[shaka.util.Error.Code.BAD_HTTP_STATUS]: () => 'The preview could not be fetched — its links may have expired',
	// The request never completed as far as the page is concerned — usually a bucket with no CORS rule for this origin.
	[shaka.util.Error.Code.HTTP_ERROR]: (error) =>
		`Could not fetch the preview media from ${hostOf(error)} — the browser blocked the response, which usually means that bucket has no CORS rule for this site`,
	[shaka.util.Error.Code.TIMEOUT]: (error) => `Timed out fetching the preview media from ${hostOf(error)}`,
};

/** S3 answers an expired presigned URL with an error status, which Shaka reports as a bad HTTP status. */
function isExpiredMediaUrlError(error: unknown): boolean {
	return error instanceof shaka.util.Error && error.code === shaka.util.Error.Code.BAD_HTTP_STATUS;
}

/** Put a renewed preview back where the expired one stopped. */
function resumeFrom(video: HTMLVideoElement, resumePoint: ResumePoint | undefined): void {
	if (!resumePoint) return;
	// eslint-disable-next-line no-param-reassign
	video.currentTime = resumePoint.time;
	if (resumePoint.playing) video.play().catch(() => undefined);
}

/** Name the cause rather than the number: a refused licence and an expired URL call for different action. */
function describeShakaError(error: unknown, licenseServers: LicenseServers): string {
	if (!(error instanceof shaka.util.Error)) return 'Could not load the stream.';

	const reason = SHAKA_ERROR_REASONS[error.code]?.(error, licenseServers) ?? 'Could not load the stream';
	const status = httpStatusOf(error);
	return `${reason} (${status ? `HTTP ${status}, ` : ''}shaka error ${error.code}).`;
}

// Every Mezz Proxy is encrypted and the proxy issues only Widevine and PlayReady, so Safari has no fallback (WH-4874).
function noUsableKeySystem(licenseServers: LicenseServers): string {
	return `This browser cannot play DRM-protected previews — it supports none of the licences the proxy issues (${offeredSystemNames(licenseServers)}). Safari needs FairPlay, which the proxy does not produce yet`;
}

/** The systems the response actually offers a licence endpoint for, named the way a person would. */
function offeredSystemNames(licenseServers: LicenseServers): string {
	const names = offeredLicenseServers(licenseServers).map(([system]) => DRM_SYSTEM_LABELS[system]);
	return names.length > 0 ? names.join(' or ') : 'any DRM system';
}

/** Network errors carry the failing request's URI first in their data. */
function hostOf(error: shaka.util.Error): string {
	const uri = error.data?.find((value: unknown) => typeof value === 'string');
	if (typeof uri !== 'string') return 'the services bucket';
	try {
		return new URL(uri).host;
	} catch {
		return 'the services bucket';
	}
}

/** Shaka nests the failing HTTP request's error inside the DRM error's data. */
function httpStatusOf(error: shaka.util.Error): number | undefined {
	const status = error.data?.find((value: unknown) => typeof value === 'number' && value >= 100 && value < 600);
	if (typeof status === 'number') return status;

	const nested = error.data?.find((value: unknown) => value instanceof shaka.util.Error);
	return nested instanceof shaka.util.Error ? httpStatusOf(nested) : undefined;
}

const PlayerWrapper = styled.div(
	({ theme }) => css`
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(1)};
	`,
);

const PlayerSlot = styled.div`
	width: 100%;
`;

/** Shown only when the slot is empty, i.e. the player really is in another window. */
const SeparateWindowNote = styled.span(
	({ theme }) => css`
		color: ${theme.palette.text.secondary};
		font-size: ${theme.typography.bodySmallRegular.fontSize};
		font-weight: ${theme.typography.bodySmallRegular.fontWeight};
	`,
);

/** Sits directly under the video, outside the Shaka overlay, so the control bar never covers it. */
const DrmBadge = styled.span(
	({ theme }) => css`
		align-items: center;
		color: ${theme.palette.text.secondary};
		display: flex;
		font-size: ${theme.typography.bodySmallRegular.fontSize};
		font-weight: ${theme.typography.bodySmallRegular.fontWeight};
		gap: ${theme.spacing(0.5)};
	`,
);
